import { useState, useMemo, useCallback } from 'react';
import { SortDirection, SortParams } from '@antimatterhq/ui/types/TableSort';

export function useTableColumnSorting<T>(items: T[]) {
  const [sortKey, setSortKey] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Sort the items based on current sort state
  const sortedItems = useMemo(() => {
    if (!sortKey) return items;

    return [...items].sort((a, b) => {
      const aValue = a[sortKey as keyof T];
      const bValue = b[sortKey as keyof T];

      if (aValue === undefined || bValue === undefined) return 0;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        const comparison = aValue.getTime() - bValue.getTime();
        return sortDirection === 'asc' ? comparison : -comparison;
      } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        const comparison = (aValue ? 1 : 0) - (bValue ? 1 : 0);
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        const comparison = Number(aValue) - Number(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });
  }, [items, sortKey, sortDirection]);

  // Handle sort changes
  const setSortBy = useCallback(
    (key: string) => {
      if (sortKey === key) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key as keyof T);
        setSortDirection('asc');
      }
    },
    [sortKey],
  );

  // Clear sorting
  const clearSort = useCallback(() => {
    setSortKey(null);
    setSortDirection('asc');
  }, []);

  // Get formatted sort params
  const sortParams = useMemo<SortParams>(() => {
    if (!sortKey) return undefined;

    return {
      key: sortKey as string,
      direction: sortDirection,
    };
  }, [sortKey, sortDirection]);

  return {
    sortedItems,
    sortKey,
    sortDirection,
    setSortBy,
    clearSort,
    sortParams,
    hasSort: sortKey !== null,
  };
}
